// P1.1: canonical public-URL sharing helpers (link sharing only — no OAuth/APIs/tokens).
import { api } from "@/lib/api";

// Shared links must always resolve to the canonical PRODUCTION public site,
// never the preview/staging host.
const SITE = (process.env.REACT_APP_PUBLIC_SITE_URL || "https://propconnectai.com").replace(/\/+$/, "");

export const publicSiteUrl = () => SITE;
export const listingShareUrl = (id) => `${SITE}/listing/${id}`;
export const buyerNeedShareUrl = (id) => `${SITE}/buyer-need/${id}`;
export const proShareUrl = (slug) => `${SITE}/pro/${slug}`;
export const qrIdentityUrl = (publicId) => `${SITE}/q/${publicId}`;

// Standard web share targets (no auth, no API). Open in a new tab.
export const shareTargets = ({ url, text }) => {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text || "");
  // Text-channel message keeps the canonical URL — the URL is always preserved verbatim.
  const msg = encodeURIComponent((text ? text + "\n" : "") + url);
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    x: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
    // Messenger web dialog requires an app_id; the fb-messenger:// deep link handles
    // installed apps (mobile). Desktop/no-app callers fall back to native share / copy.
    messenger: `fb-messenger://share?link=${u}`,
    whatsapp: `https://wa.me/?text=${msg}`,
    viber: `viber://forward?text=${msg}`,
  };
};

export const openShareWindow = (href) => {
  // Plain new-tab open (NO sized "width/height" popup features — those are frequently
  // blocked or silently no-op on iOS Safari and in-app/webview browsers). Returns whether
  // a window was actually opened so callers can fall back gracefully.
  try {
    const w = window.open(href, "_blank");
    if (w) { try { w.opener = null; } catch (_) { /* noop */ } return true; }
  } catch (e) { /* fall through */ }
  return false;
};

export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.focus(); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) { return false; }
};

export const canNativeShare = () => typeof navigator !== "undefined" && typeof navigator.share === "function";

// Returns "shared" | "unsupported" | "cancelled"
// Only includes defined fields — a Facebook url-only share must not carry empty title/text
// keys, or FB's iOS extension may treat it as a text status and skip the rich-link unfurl.
export const nativeShare = async ({ url, title, text } = {}) => {
  if (!canNativeShare()) return "unsupported";
  const data = {};
  if (url) data.url = url;
  if (title) data.title = title;
  if (text) data.text = text;
  try {
    await navigator.share(data);
    return "shared";
  } catch (e) {
    return e && e.name === "AbortError" ? "cancelled" : "unsupported";
  }
};

// Robust social handoff for the Facebook/LinkedIn/X buttons.
// Tries a normal new tab; if the browser blocks it (returns null), falls back to the OS
// share sheet, then to copying the canonical link. Returns:
// "opened" | "native" | "cancelled" | "copied" | "failed"
export const webShareOrFallback = async (href, { url, title, text } = {}) => {
  if (openShareWindow(href)) return "opened";
  if (canNativeShare()) {
    const res = await nativeShare({ url, title, text });
    if (res === "shared") return "native";
    if (res === "cancelled") return "cancelled";
  }
  const ok = await copyToClipboard(url || href);
  return ok ? "copied" : "failed";
};

// True on touch devices (phones/tablets) where in-app webviews are common.
export const isCoarsePointer = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

// ---- Centralized professional share payload (single source of truth for both
// /my-card and /pro/{slug}). Short, clean copy; never includes email/mobile. ----
export const PRO_SHARE_TEXT = "View my professional profile on PropertyConnect.";
export const proShareTitle = ({ name, position } = {}) =>
  `${name || "Real Estate Professional"}${position ? ` | ${position}` : ""} — PropertyConnect`;
// Message body for text-based channels (WhatsApp/Viber). The canonical URL is appended
// ONCE by the caller — do not embed it here (avoids a duplicated URL).
export const proShareMessage = ({ name, position } = {}) =>
  `${name || "Real Estate Professional"}${position ? ` | ${position}` : ""}\n${PRO_SHARE_TEXT}`;

// Mobile-first primary share. On touch devices with Web Share, share natively (lets the OS
// surface Messages/Messenger/WhatsApp/Viber/Facebook etc.); otherwise open the branded menu.
// url is ALWAYS the canonical /pro/{slug}. Returns "shared" | "cancelled" | "menu".
export const openProShare = async ({ url, name, position, openMenu } = {}) => {
  if (canNativeShare() && isCoarsePointer()) {
    // URL-ONLY: iOS/Messenger turns `text` into a separate message bubble while `url`
    // generates the rich professional link-preview. A clean { url } yields ONE rich preview.
    const res = await nativeShare({ url });
    if (res === "shared") return "shared";
    if (res === "cancelled") return "cancelled";
  }
  if (typeof openMenu === "function") openMenu();
  return "menu";
};

// Facebook-specific handoff. facebook.com/sharer is HOSTILE inside iOS/in-app webviews:
// window.open can return a truthy WindowProxy while the composer then fails for the user
// (unlike LinkedIn/X sharers which tolerate webviews). So on touch devices with Web Share
// we PREFER the native Share Sheet (user selects the installed Facebook app). Desktop keeps
// the normal Facebook web sharer. Never claims a post was published; a cancelled sheet is
// not a success. Returns: "native" | "cancelled" | "opened" | "copied" | "failed"
// NOTE: the native branch shares URL-ONLY. Facebook's iOS share extension treats a payload
// carrying `text`/`title` as a status composition and embeds the link inside the body — which
// it does NOT unfurl (composer shows only the domain). A clean { url } triggers FB's normal
// rich-link scrape (cover photo + title + canonical URL). Canonical URL is preserved.
export const facebookShare = async ({ url } = {}) => {
  const sharer = shareTargets({ url }).facebook;
  let triedNative = false;
  if (canNativeShare() && isCoarsePointer()) {
    triedNative = true;
    const res = await nativeShare({ url });
    if (res === "shared") return "native";
    if (res === "cancelled") return "cancelled";
    // unsupported/error → fall through to the web sharer / copy chain
  }
  if (openShareWindow(sharer)) return "opened";
  if (!triedNative && canNativeShare()) {
    const res = await nativeShare({ url });
    if (res === "shared") return "native";
    if (res === "cancelled") return "cancelled";
  }
  const ok = await copyToClipboard(url);
  return ok ? "copied" : "failed";
};

// Pre-warm the edge crawler cache for a public listing so Facebook's first scrape is fast.
// Fire-and-forget; server bounds it (public listings with photos, once per 10 min).
export const warmSharePreview = (url) => {
  if (!url || !url.startsWith(`${SITE}/listing/`)) return;
  api.post("/share/warm", { path: url.slice(SITE.length) }).catch(() => {});
};

// Accurate og:image metadata for a listing cover: secure_url, type and (when known) the
// served image's real dimensions (web variant = original scaled to ≤1600px, never upscaled).
export const listingImageMeta = (p, img) => {
  const ext = (img.split("?")[0].split(".").pop() || "").toLowerCase();
  const type = { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif" }[ext];
  const ph = (p.photo_urls || []).find((x) => x.url === img);
  let width, height;
  if (ph?.width && ph?.height) {
    const scale = img.endsWith(".web.webp") ? Math.min(1, 1600 / Math.max(ph.width, ph.height)) : 1;
    width = Math.max(1, Math.floor(ph.width * scale)); height = Math.max(1, Math.floor(ph.height * scale));
  }
  return { type, width, height };
};
